"use client"

import { useState } from "react"
import {
  ArrowLeft,
  Users,
  TrendingUp,
  Target,
  Award,
  Eye,
  Download,
  RefreshCw,
  Star,
  AlertTriangle,
  Grid3X3,
  List,
} from "lucide-react"
import "./BusinessInsightsPage.css"
import { useNavigate } from "react-router-dom";
import BackButton from "../shared/backButton";

const BusinessInsightsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [viewMode, setViewMode] = useState("list") // 'cards' or 'list'

  const categories = [
    { value: "all", label: "All Insights" },
    { value: "employees", label: "Employee Evaluation" },
    { value: "teams", label: "Team Performance" },
    { value: "growth", label: "Growth Opportunities" },
  ]

  const employeeInsights = [
    {
      id: 1,
      title: "Top Performers Analysis",
      description: "Comprehensive evaluation of highest performing employees and their key success factors",
      category: "employees",
      type: "Performance",
      priority: "high",
      icon: Star,
      color: "business-primary",
      metrics: {
        topPerformers: 23,
        avgPerformance: "94%",
        retentionRate: "96%",
        promotionReady: 8,
      },
      insights: [
        "Top performers show 40% higher engagement scores",
        "Strong correlation between training completion and performance",
        "Leadership potential identified in 35% of top performers",
      ],
    },
    {
      id: 2,
      title: "At-Risk Employee Assessment",
      description: "Early identification of employees showing signs of disengagement or performance decline",
      category: "employees",
      type: "Risk Assessment",
      priority: "critical",
      icon: AlertTriangle,
      color: "business-secondary",
      metrics: {
        atRiskEmployees: 12,
        riskFactors: 5,
        interventionSuccess: "78%",
        avgTenure: "2.3 years",
      },
      insights: [
        "Workload imbalance is the primary risk factor",
        "Early intervention increases retention by 65%",
        "Career development discussions reduce risk by 45%",
      ],
    },
    {
      id: 3,
      title: "Skills Development Tracking",
      description: "Monitor employee skill progression and identify development opportunities",
      category: "employees",
      type: "Development",
      priority: "medium",
      icon: TrendingUp,
      color: "business-tertiary",
      metrics: {
        skillsTracked: 156,
        improvementRate: "23%",
        trainingHours: "1,240",
        certifications: 45,
      },
      insights: [
        "Technical skills show fastest improvement rates",
        "Mentorship programs accelerate skill development",
        "Cross-functional training increases adaptability",
      ],
    },
  ]

  const teamInsights = [
    {
      id: 4,
      title: "Team Collaboration Index",
      description: "Evaluate team dynamics, communication effectiveness, and collaborative success",
      category: "teams",
      type: "Collaboration",
      priority: "high",
      icon: Users,
      color: "business-quaternary",
      metrics: {
        collaborationScore: "87%",
        crossTeamProjects: 24,
        communicationRating: "4.2/5",
        conflictResolution: "92%",
      },
      insights: [
        "Remote teams show 15% higher collaboration scores",
        "Regular team building improves project outcomes",
        "Clear role definition reduces conflicts by 60%",
      ],
    },
    {
      id: 5,
      title: "Team Performance Benchmarking",
      description: "Compare team performance metrics against organizational and industry standards",
      category: "teams",
      type: "Benchmarking",
      priority: "medium",
      icon: Target,
      color: "business-primary",
      metrics: {
        avgTeamPerformance: "89%",
        industryBenchmark: "82%",
        topPerformingTeams: 3,
        improvementOpportunities: 5,
      },
      insights: [
        "Teams exceed industry average by 7%",
        "Agile teams show 20% better performance",
        "Diverse teams demonstrate higher innovation scores",
      ],
    },
    {
      id: 6,
      title: "Leadership Pipeline Assessment",
      description: "Identify and evaluate potential leaders within teams for succession planning",
      category: "teams",
      type: "Leadership",
      priority: "high",
      icon: Award,
      color: "business-secondary",
      metrics: {
        leadershipCandidates: 18,
        readinessScore: "76%",
        successionCoverage: "85%",
        developmentPrograms: 6,
      },
      insights: [
        "Strong internal leadership pipeline identified",
        "Mentorship programs increase leadership readiness",
        "Cross-functional experience enhances leadership potential",
      ],
    },
  ]

  const allInsights = [...employeeInsights, ...teamInsights]

  const filteredInsights =
    selectedCategory === "all" ? allInsights : allInsights.filter((insight) => insight.category === selectedCategory)

  const handleRefresh = () => {
    setIsRefreshing(true)
    setTimeout(() => {
      setIsRefreshing(false)
    }, 2000)
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical":
        return "business-priority-critical"
      case "high":
        return "business-priority-high"
      case "medium":
        return "business-priority-medium"
      default:
        return "business-priority-low"
    }
  }

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleViewDetails = (insight) => {
    onNavigate("/reports/detailed-business-insights/:id", insight)
  }

  const renderCardView = () => (
    <div className="business-insights-container-grid">
      {filteredInsights.map((insight) => {
        const IconComponent = insight.icon
        return (
          <div key={insight.id} className={`business-insight-card-wrapper ${insight.color}`}>
            <div className="business-card-header-section">
              <div className="business-card-icon-container">
                <IconComponent size={24} />
              </div>
              <div className="business-card-meta-wrapper">
                <span className={`business-priority-badge ${getPriorityColor(insight.priority)}`}>
                  {insight.priority}
                </span>
                <span className="business-insight-type-label">{insight.type}</span>
              </div>
            </div>

            <div className="business-card-content-section">
              <h3>{insight.title}</h3>
              <p>{insight.description}</p>

              <div className="business-insight-metrics-wrapper">
                <div className="business-metrics-grid-layout">
                  {Object.entries(insight.metrics).map(([key, value]) => (
                    <div key={key} className="business-metric-item-container">
                      <span className="business-metric-label-text">
                        {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                      </span>
                      <span className="business-metric-value-text">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="business-insight-details-wrapper">
                <h4>Key Insights:</h4>
                <ul className="business-insights-list-container">
                  {insight.insights.map((item, index) => (
                    <li key={index} className="business-insight-list-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="business-card-footer-section">
              <div className="business-card-actions-wrapper">
                <button className="business-action-btn-item" onClick={() => handleViewDetails(insight)}>
                  <Eye size={16} />
                  View Details
                </button>
                <button className="business-action-btn-item">
                  <Download size={16} />
                  Export
                </button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  const renderListView = () => (
    <div className="business-insights-table">
      <div className="business-table-header">
        <div className="business-table-cell">Insight</div>
        <div className="business-table-cell">Category</div>
        <div className="business-table-cell">Type</div>
        <div className="business-table-cell">Priority</div>
        <div className="business-table-cell">Key Metrics</div>
        <div className="business-table-cell">Actions</div>
      </div>
      {filteredInsights.map((insight) => {
        const IconComponent = insight.icon
        return (
          <div key={insight.id} className="business-table-row" onClick={() => handleViewDetails(insight)}>
            <div className="business-table-cell business-insight-info">
              <div className={`business-list-icon ${insight.color}`}>
                <IconComponent size={20} />
              </div>
              <div className="business-insight-details">
                <h4>{insight.title}</h4>
                <p>{insight.description}</p>
              </div>
            </div>
            <div className="business-table-cell">
              <span className="business-category-badge">{insight.category}</span>
            </div>
            <div className="business-table-cell">
              <span className="business-type-badge">{insight.type}</span>
            </div>
            <div className="business-table-cell">
              <span className={`business-priority-badge ${getPriorityColor(insight.priority)}`}>
                {insight.priority}
              </span>
            </div>
            <div className="business-table-cell">
              <div className="business-metrics-summary">
                {Object.entries(insight.metrics)
                  .slice(0, 2)
                  .map(([key, value]) => (
                    <span key={key} className="business-metric-summary">
                      {value}
                    </span>
                  ))}
              </div>
            </div>
            <div className="business-table-cell">
              <div className="business-table-actions">
                <button
                  className="business-table-action-btn"
                  onClick={() => handleViewDetails(insight)}
                  title="View Details"
                >
                  <Eye size={16} />
                </button>
                <button className="business-table-action-btn" title="Export">
                  <Download size={16} />
                </button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="business-insights-dashboard-page">
      <BackButton />

      <main className="business-insights-dashboard-main">
        <div className="container">
          {/* Header Section */}
          <div className="business-insights-dashboard-header">
            <div className="business-header-content-wrapper">
              <div className="business-header-icon-container">
                <TrendingUp size={40} />
              </div>
              <div className="business-header-text-container">
                <h1>Business Insights Report</h1>
                <p>Employee and team evaluation insights for strategic decision making</p>
              </div>
            </div>
            <div className="business-header-controls-wrapper">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="business-category-select"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              <div className="business-view-toggle">
                <button
                  className={`business-view-btn ${viewMode === "cards" ? "active" : ""}`}
                  onClick={() => setViewMode("cards")}
                >
                  <Grid3X3 size={16} />
                  Cards
                </button>
                <button
                  className={`business-view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                >
                  <List size={16} />
                  List
                </button>
              </div>
              <button className="business-refresh-btn" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw size={16} className={isRefreshing ? "business-spinning" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="business-summary-stats-section">
            <div className="business-stats-grid-container">
              <div className="business-stat-card-item business-stat-employees">
                <div className="business-stat-icon-container">
                  <Users size={24} />
                </div>
                <div className="business-stat-content-wrapper">
                  <div className="business-stat-value-text">156</div>
                  <div className="business-stat-label-text">Employees Evaluated</div>
                </div>
              </div>
              <div className="business-stat-card-item business-stat-teams">
                <div className="business-stat-icon-container">
                  <Target size={24} />
                </div>
                <div className="business-stat-content-wrapper">
                  <div className="business-stat-value-text">8</div>
                  <div className="business-stat-label-text">Teams Analyzed</div>
                </div>
              </div>
              <div className="business-stat-card-item business-stat-performance">
                <div className="business-stat-icon-container">
                  <TrendingUp size={24} />
                </div>
                <div className="business-stat-content-wrapper">
                  <div className="business-stat-value-text">89%</div>
                  <div className="business-stat-label-text">Avg Performance</div>
                </div>
              </div>
              <div className="business-stat-card-item business-stat-insights">
                <div className="business-stat-icon-container">
                  <Award size={24} />
                </div>
                <div className="business-stat-content-wrapper">
                  <div className="business-stat-value-text">23</div>
                  <div className="business-stat-label-text">Key Insights</div>
                </div>
              </div>
            </div>
          </div>

          {/* Insights Grid */}
          <div className="business-insights-grid-section">
            <h2>Evaluation Insights</h2>
            {viewMode === "cards" ? renderCardView() : renderListView()}
          </div>
        </div>
      </main>
    </div>
  )
}

export default BusinessInsightsPage
